import React, { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useGetFirebaseStore } from "../../service/hooks/useGetFirebaseStore";
import {
  categoryMeta,
  workHref,
  workPath,
} from "../../utils/workRoutes";
import { SiteHeader } from "./SiteHeader";
import "./LandingGrid.css";
import "./WorkDetail.css";

const canLoadImage = (src) =>
  Boolean(src) &&
  !/firebasestorage\.googleapis\.com|appspot\.com/i.test(src);

function safeDecode(value) {
  const raw = String(value || "");
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

export default function WorkDetail() {
  const { category, workId } = useParams();
  const decodedId = safeDecode(workId);
  const decodedCategory = safeDecode(category);
  const meta = categoryMeta(decodedCategory);
  const { data = [], loading } = useGetFirebaseStore(decodedCategory);

  const { work, prev, next } = useMemo(() => {
    const list = Array.isArray(data) ? data : [];
    const index = list.findIndex((item) => String(item.id) === decodedId);
    return {
      work: index >= 0 ? list[index] : null,
      prev: index > 0 ? list[index - 1] : null,
      next: index >= 0 && index < list.length - 1 ? list[index + 1] : null,
    };
  }, [data, decodedId]);

  const href = workHref(work);
  const image = canLoadImage(work?.ImageUrl) ? work.ImageUrl : "";
  const producer = work?.producer || work?.Producer || "";
  const date = work?.date || work?.Date || work?.["date:"] || "";

  return (
    <div className="lg-page">
      <section className="lg-root wd-root" aria-label={work?.title || "Work"}>
        <div className="lg-grid-bg" aria-hidden="true" />

        <SiteHeader />

        <span className="lg-cross lg-cross--a" aria-hidden="true">+</span>
        <span className="lg-cross lg-cross--b" aria-hidden="true">+</span>
        <span className="lg-cross lg-cross--d" aria-hidden="true">+</span>
        <div className="lg-divider" aria-hidden="true" />
        <p className="lg-vertical wd-vertical">{meta.title}</p>

        <main className="wd-main">
          <div className="wd-media" aria-hidden={!image}>
            {image ? <img src={image} alt="" /> : null}
          </div>

          <div className="wd-body">
            {loading && <p className="wd-status">Loading…</p>}

            {!loading && !work && (
              <>
                <p className="lg-section-kicker">
                  <span>404</span>
                  <span className="lg-section-kicker-rule" aria-hidden="true" />
                  <span>NOT FOUND</span>
                </p>
                <h1 className="wd-title">This work is not in the archive.</h1>
                <Link to="/works" className="lg-section-link">
                  Back to works
                </Link>
              </>
            )}

            {work && (
              <>
                <p className="lg-section-kicker">
                  <span>{meta.index || "00"}</span>
                  <span className="lg-section-kicker-rule" aria-hidden="true" />
                  <span>{meta.title}</span>
                </p>
                <h1 className="wd-title">{work.title || "Untitled"}</h1>
                {work.description ? (
                  <p className="wd-copy">{work.description}</p>
                ) : null}

                <dl className="wd-meta">
                  {producer ? (
                    <div>
                      <dt>Producer</dt>
                      <dd>{producer}</dd>
                    </div>
                  ) : null}
                  {date ? (
                    <div>
                      <dt>Date</dt>
                      <dd>{date}</dd>
                    </div>
                  ) : null}
                </dl>

                {href ? (
                  <a
                    className="wd-watch"
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Watch →
                  </a>
                ) : (
                  <p className="wd-nolink">Link coming soon</p>
                )}

                <div className="wd-nav">
                  <Link
                    to={`/works?category=${decodedCategory}`}
                    className="lg-section-link"
                  >
                    All {meta.title}
                  </Link>
                  {prev ? (
                    <Link
                      to={workPath(decodedCategory, prev.id)}
                      className="lg-section-link"
                    >
                      ← Prev
                    </Link>
                  ) : null}
                  {next ? (
                    <Link
                      to={workPath(decodedCategory, next.id)}
                      className="lg-section-link"
                    >
                      Next →
                    </Link>
                  ) : null}
                </div>
              </>
            )}
          </div>
        </main>
      </section>
    </div>
  );
}
